import {inject, init} from '@injex/core';
import BaseItemModel from '../../../../../common/base/BaseItemModel';
import {makeObservable, observable} from 'mobx';
import {IScheduleReport} from '../../../interfaces/IScheduleReport';
import {ReportVerticalType} from '../../../../../common/enums/ReportVerticalType';
import UserManager from '../../../../../common/managers/UserManager.mdl';
import IReportingFilter from '../../../../reporting/common/interfaces/IReportingFilter';
import {guid} from '@vidazoo/ui-kit';
import IReportingConstraint from '../../../../reporting/common/interfaces/IReportingConstraint';
import ReportingFiltersManager from '../../../../../common/reporting-filters/managers/ReportingFiltersManager.mdl';
import {clone} from 'lodash';
import {AlertAccent} from '../../../../../common/alerts/common/enums';
import {RouterService} from "../../../../../common/router/services/routerService.mdl";
import {SessionManager} from '../../../../session/managers/sessionManager.mdl';

export default abstract class BaseScheduleReportModel extends BaseItemModel<IScheduleReport> {

    @inject() public userManager: UserManager;
    @inject() public reportingFiltersManager: ReportingFiltersManager;
    @inject() public routerService: RouterService;
    @inject() public sessionManager: SessionManager;

    @observable public runNowRecipients: string[];

    protected itemType: string = "schedule report";

    constructor() {
        super();
        this.runNowRecipients = []
        makeObservable(this);
    }

    @init()
    protected initialize() {
        this.reset();
    }

    public reset() {
        this.item.data = this.defaultValue;
        const email = this.sessionManager?.email || this.userManager?.email;
        this.item.data.recipients = email ? [email] : [];
        this.runNowRecipients = email ? [email] : [];
    }

    public get defaultValue(): IScheduleReport {
        return {
            name: "",
            description: "",
            _id: '',
            created: 0,
            date: 0,
            error: '',
            isActive: true,
            isDeleted: false,
            lastSchedule: 0,
            network: undefined,
            nextSchedule: 0,
            updated: 0,
            user: undefined,
            schedule: "",
            dateRange: "",
            timezone: "",
            fields: observable([]),
            groups: observable([]),
            filters: observable([]),
            constraints: observable([]),
            delimiter: ";",
            sendEmpty: true,
            recipients: [],
            dataSource: "user",
            dataSourceIds: [],
            verticalType: ReportVerticalType.PLATFORM
        }
    }

    public addFilter = (): IReportingFilter => {
        const filter = observable<IReportingFilter>({
            id: guid(),
            key: "",
            values: [],
            filterList: [],
            isLoading: false,
            filterValueKey: "",
            filterLabelKey: "",
            allowNew: false,
            exclude: false,
            operator: "",
        });

        this.item.data.filters.push(filter);

        return filter;
    };

    public addConstraint = (values?: Partial<IReportingConstraint>) => {
        this.item.data.constraints = this.item.data.constraints.concat({
            id: guid(),
            name: "",
            op: "",
            value: "",
            ...values
        });

        return this.item.data.constraints[this.item.data.constraints.length - 1];
    };

    public setFilterParam = (filter: IReportingFilter, key: string, value: any) => {
        if (key === "key") {
            this.setFilterKey(filter, value);
            return;
        }

        filter[key] = value;
    };

    public setFilterKey = (filter: IReportingFilter, key: string): Promise<any> => {
        const filterHandler = this.reportingFiltersManager.getFilter(key, this.item.data.verticalType);

        if (!filterHandler) {
            return;
        }

        filter.key = key;
        filter.filterLabelKey = filterHandler.labelKey;
        filter.filterValueKey = filterHandler.valueKey;
        filter.allowNew = filterHandler.allowNew;
        filter.isLoading = filterHandler.isLoading;
        filter.values = [];

        return filterHandler.initialize().then(() => {
            filter.filterList = filterHandler.items;
            filter.isLoading = filterHandler.isLoading;
        });
    };

    public setConstraintParam = (constraint: IReportingConstraint, key: string, value: any) => {
        constraint[key] = value;
    };

    public clearAllConstraint = () => {
        this.item.data.constraints = [];
    }

    public clearAllFilters = () => {
        this.item.data.filters = [];
    }

    public pushFilterValue = (filter: IReportingFilter, value: string, label: string) => {
        let item: any = value;

        if (filter.filterLabelKey && filter.filterValueKey) {
            item = {
                [filter.filterLabelKey]: label,
                [filter.filterValueKey]: value
            };
        }

        filter.values.push(item)
    };

    public removeFilterValue = (filter: IReportingFilter, value: string) => {
        filter.values = filter.filterValueKey
            ? filter.values.filter((item) => item[filter.filterValueKey] !== value)
            : filter.values.filter((item) => item !== value);
    };

    public addToRunNowRecipients = (item: any) => {
        this.runNowRecipients = [...this.runNowRecipients, item];
    };

    public removeFromRunNowRecipients = (value: string, prop: string = "value") => {
        this.runNowRecipients = this.runNowRecipients.filter((item) => (prop ? item[prop] : item) !== value);
    };

    public async runNow() {
        try {
            let data = this.submitify();
            data = {...data, recipients: this.runNowRecipients};
            await this.apiManager.scheduleReports.runNow(data._id, data);

            this.alertsManager.setBasicAlert({
                content: "Operation Complete, Your report was generated successfully",
                ttl: 3000
            }, AlertAccent.Success);

        } catch (err) {
            this.alertsManager.setBasicAlert({
                content: "Operation Failed, Failed to get your report",
                ttl: 3000
            }, AlertAccent.Error);
        }
    }

    protected submitify(item?: IScheduleReport) {
        const cloneItem: any = clone(item || this.item.data);

        cloneItem.groups = cloneItem.groups.map((x) => ({value: x.value, label: x.label}));
        cloneItem.fields = cloneItem.fields.map((x) => ({value: x.value, label: x.label}));
        cloneItem.constraints = cloneItem.constraints.map((x) => ({name: x.name, op: x.op, value: x.value}));
        cloneItem.filters = cloneItem.filters.map((x) => {
            const filter = this.reportingFiltersManager.getFilter(x.key, this.item.data.verticalType);

            return filter.reportify(x);
        });

        return cloneItem;
    }

}
